import ThemedSelect from "../shared/ThemedSelect.jsx";
import { Col, Form, Row } from "react-bootstrap";
import { CLAIM_SECTIONS, PEOPLE_SOURCES, fieldContext, visibleFields } from "./claimFields.js";
import { rowLabel, selectableRows } from "../../utils/people.js";

// Edit form for a claim, rendered from CLAIM_SECTIONS. Parent owns form state and
// supplies dropdown options (opts, keyed by source) + users. State-dependent
// fields (visibleWhen) resolve against the CURRENT form values, so switching the
// Job Type live-toggles e.g. Tender Due Date / the callout QR select.

// PEOPLE sources carry `full_name`; lookup sources carry `name`. Inspector is a
// DIFFERENT list from Project Co-Ordinator — both were `source: "users"` and both
// pages loaded only the PCO list, so the Inspector dropdown had always offered
// co-ordinators (Karen Sweatman, 2026-08-13: "inspector drop down bringing up PCO
// list instead of inspector"). Same fault the claim search carried until
// 2026-08-12; this is the second place it lived.
//
// PEOPLE_SOURCES lives in claimFields.js because CLAIM_LOOKUP_SOURCES has to
// exclude exactly this set — the two disagreeing is what broke New Claim.

// `statusCode` / `wasAccepted` come from the CLAIM, which the edit form does not
// otherwise see — `form` holds only editable fields. They gate the scheduling
// dates (claimFields `isAccepted`). New Claim passes neither and correctly
// renders the pre-accept form.
function ClaimForm({
  form,
  onChange,
  opts = {},
  users = [],
  inspectors = [],
  canEditGated = true,
  statusCode = "",
  wasAccepted = false,
}) {
  const ctx = fieldContext({
    form,
    jobTypes: opts["job-types"] || [],
    statusCode,
    wasAccepted,
  });

  function renderInput(field) {
    const value = form[field.name] ?? "";
    const disabled = field.gated && !canEditGated;
    if (field.type === "ref") {
      const all =
        field.source === "users" ? users
        : field.source === "inspectors" ? inspectors
        : opts[field.source] || [];
      const labelKey = PEOPLE_SOURCES.has(field.source) ? "full_name" : "name";
      // ASSIGNING somebody is not the same as FILTERING BY them. A retired
      // account must not be choosable — "Resource Department" held 6,076 jobs
      // only because 1.0 auto-assigned every new one to it — but the claim it is
      // already on must still say so, or 6,076 historic jobs open with a blank
      // Project Co-Ordinator and the first Save silently clears it.
      //
      // Same trick the free-text `select` below uses: keep the current value
      // selectable even when it is no longer on offer.
      const rows = selectableRows(all, value);
      return (
        <ThemedSelect name={field.name} value={value} onChange={onChange} size="sm" disabled={disabled} data-hint={disabled ? "Administrators/PowerUsers only" : undefined}>
          <option value="">— None —</option>
          {rows.map((row) => (
            <option key={row.id} value={row.id}>
              {rowLabel(row, labelKey)}
            </option>
          ))}
        </ThemedSelect>
      );
    }
    if (field.type === "select") {
      const options = (field.options || []).map((o) =>
        typeof o === "string" ? { value: o, label: o } : o
      );
      // A legacy free-text value stays selectable rather than silently vanishing.
      const known = options.some((o) => o.value === value);
      return (
        <ThemedSelect name={field.name} value={value} onChange={onChange} size="sm">
          <option value="">— None —</option>
          {!known && value ? <option value={value}>{value}</option> : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </ThemedSelect>
      );
    }
    // A two-state FACT, where both states say something. A checkbox cannot: its
    // unticked state is ambiguous between "no" and "nobody has said", and on
    // Lockbox Picked Up those are very different (Robert, 15 Aug). Two labelled
    // radios put the answer on screen either way round.
    if (field.type === "radio") {
      const checked = value === true || value === "true";
      return (
        <div className="d-flex gap-3">
          {[
            { on: true, label: field.onLabel || "Yes" },
            { on: false, label: field.offLabel || "No" },
          ].map((opt) => (
            <Form.Check
              key={String(opt.on)}
              type="radio"
              id={`claim-${field.name}-${opt.on}`}
              name={field.name}
              label={opt.label}
              checked={checked === opt.on}
              // A STRING, like the `check` type stores, so both change handlers
              // and the server's coercion see the shape they already expect.
              onChange={() =>
                onChange({ target: { name: field.name, type: "radio", value: String(opt.on) } })
              }
            />
          ))}
        </div>
      );
    }
    if (field.type === "check") {
      return (
        <Form.Check
          type="switch"
          id={`claim-${field.name}`}
          name={field.name}
          checked={value === true || value === "true"}
          onChange={onChange}
          label=""
        />
      );
    }
    if (field.type === "textarea") {
      return (
        <Form.Control as="textarea" rows={2} size="sm" name={field.name} value={value} onChange={onChange} />
      );
    }
    return (
      <Form.Control
        type={field.type === "date" ? "date" : "text"}
        size="sm"
        // A date or a money figure has a natural width. Stretched across half
        // the page it reads as a mistake, and it is what made the form sprawl.
        style={field.type === "date" ? { maxWidth: 170 } : field.narrow ? { maxWidth: 220 } : undefined}
        name={field.name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        data-hint={disabled ? "Administrators/PowerUsers only" : undefined}
      />
    );
  }

  function renderSection(section) {
    const fields = visibleFields(section, ctx);
    if (fields.length === 0) return null;
    const body = (
      <Row className="g-2">
        {fields.map((field) => (
          <Col xs={12} md={field.wide ? 12 : 6} key={field.name}>
            {/* SAME GEOMETRY AS THE READ VIEW (Robert 2026-08-05): label in a
                150px column on the left, control to its right. Stacking the
                label above a full width input made the edit form roughly twice
                the height of the page it replaces, so clicking Edit changed the
                shape of everything and pushed the form over two screens.
                `align-items-start` keeps the label at the top of a textarea. */}
            <Form.Group className="d-flex align-items-start">
              {/* `hint` explains what a field MEANS, on hover. Added for the two
                  go-ahead dates (Karen Sweatman 2026-08-12 — "are these picking
                  up the correct dates & reword to make clearer"), where the
                  names alone could not carry the difference between when the job
                  was accepted and when the client was told. Use it wherever a
                  label is honest but not sufficient. */}
              <Form.Label
                className="text-muted me-2 mb-0 pt-1"
                style={{ minWidth: 150, cursor: field.hint ? "help" : undefined }}
                data-hint={field.hint || undefined}
              >
                {field.label}
                {field.required ? <span className="text-danger"> *</span> : null}
              </Form.Label>
              <div className="flex-grow-1">{renderInput(field)}</div>
            </Form.Group>
          </Col>
        ))}
      </Row>
    );
    // The read view uses the identical markup and class — the two are the same
    // page in two modes, and a heading that differed between them would show it.
    // See ClaimView for why `open` is safe to pass this way.
    return (
      <details
        key={section.title}
        className="mb-3 p-3 claim-section"
        open={!section.startClosed}
      >
        <summary className="claim-section-title text-uppercase fw-semibold small border-bottom pb-1 mb-2">
          {section.title}
        </summary>
        {body}
      </details>
    );
  }

  return <>{CLAIM_SECTIONS.map(renderSection)}</>;
}

export default ClaimForm;
